// useTeamSchedulePrint.ts - Custom hook for team schedule PDF export
// CREATED: Encapsulates PDF generation state and action for the Team Schedule page.
// Exposes handlePrint(), isPrinting, and canPrint based on current filter/data state.
// UPDATED: Updated to await async generateTeamSchedulePdf (logo loading requires async).

import { useState, useCallback } from 'react';
import { generateTeamSchedulePdf } from '../lib/teamSchedulePdf';
import type { ScheduleTask, ScheduleOperator } from '../lib/teamSchedulePdf';
import { captureException } from '../lib/sentry';

interface UseTeamSchedulePrintOptions {
  siteName: string;
  date: Date;
  displayedOperators: ScheduleOperator[];
  tasks: ScheduleTask[];
}

export function useTeamSchedulePrint(options: UseTeamSchedulePrintOptions) {
  const [isPrinting, setIsPrinting] = useState(false);

  const handlePrint = useCallback(async () => {
    if (options.tasks.length === 0 || options.displayedOperators.length === 0) return;

    setIsPrinting(true);
    await new Promise<void>(resolve => setTimeout(resolve, 50));
    try {
      await generateTeamSchedulePdf({
        siteName: options.siteName,
        date: options.date,
        displayedOperators: options.displayedOperators,
        tasks: options.tasks,
      });
    } catch (error) {
      if (import.meta.env.DEV) console.error('PDF generation failed:', error);
      captureException(error, { context: 'useTeamSchedulePrint' });
    } finally {
      setIsPrinting(false);
    }
  }, [options.siteName, options.date, options.displayedOperators, options.tasks]);

  const canPrint = options.tasks.length > 0 && options.displayedOperators.length > 0;

  return { handlePrint, isPrinting, canPrint };
}
